import { List as ImmutableList, type Map as ImmutableMap } from 'immutable';
import { DateTime, Interval } from 'luxon';
import { createSelector } from 'reselect';

import { createWeakMapSelector } from 'app-components/utils/core/reselect';
import { constructPredefinedInterval } from 'app-components/utils/date';
import type RootState from 'app-components/utils/redux-store/rootState';

import { accountsSelectors } from '../accounts';
import { authSelectors } from '../auth';
import { categoriesSelectors } from '../categories';
import { selectorsHelpers } from '../core';
import { dateSelectors } from '../date';
import { featureFlagsSelectors } from '../feature-flags';
import type { FilterObject } from '../filters/filtersTypes';
import { preferencesSelectors } from '../preferences';
import { getOverTimeData, getOverTimeTotal, getReportFilteredTxns } from '../reports/reportsSelectors';
import type { ReportGroupTotal, ReportSpecificFilter } from '../reports/reportsTypes';
import { getReportSpecificFilterFromReportName } from '../reports/reportsUtils';
import { tagsSelectors } from '../tags';
import { transactionListUtils } from '../transaction-list';
import { transactionsSelectors } from '../transactions';
import type { TransactionItem, TransactionRecord } from '../transactions/transactionsTypes';
import {
  isSavedSpendingWatchlist,
  type SavedSpendingWatchlist,
  SPENDING_WATCH_LIST_REDUCER_KEY,
  type SpendingWatchlist,
  type SpendingWatchlistProps,
  type SpendingWatchlistStore,
  type Watchlist,
  type WatchlistFixedMetrics,
  type WatchlistSortOrder,
} from './spendingWatchlistTypes';
import {
  computeMonthsInRange,
  getFixedWatchlistTimeStep,
  type SeparatedWatchlistTransactions,
  separateFutureAndPastTxns,
  sortWatchList,
} from './spendingWatchlistUtils';

type State = Pick<RootState, typeof SPENDING_WATCH_LIST_REDUCER_KEY>;
type ChartState = State & Parameters<typeof getReportFilteredTxns>[0] & Parameters<typeof tagsSelectors.getTags>[0];
type SetupState = State &
  Parameters<typeof preferencesSelectors.getWatchlistSetupCompleted>[0] &
  Parameters<typeof authSelectors.getCurrentDataset>[0] &
  Parameters<typeof featureFlagsSelectors.getFeatureFlag>[0];
type FixedMetricsState = State & Parameters<typeof dateSelectors.getCurrentDateString>[0];

type WatchlistMetricsInput = Pick<
  SpendingWatchlistProps,
  'period' | 'startDate' | 'endDate' | 'spentSoFar' | 'targetAmount'
>;

// Read by every watchlist report selector.
type WatchlistReportProps = {
  id: string;
  filterObject?: FilterObject | null;
  filterType?: string;
};

export type WatchlistChartProps = WatchlistReportProps & {
  reportName: string;
  // Chart labels are daily or monthly.
  timeStep: 'days' | 'months';
  chartType?: string;
  dateRange?: Interval;
};

export type WatchlistDisplayProps = WatchlistChartProps & { groupBy?: string | null };

export type WatchlistTotalState = ChartState;
// A total renders no labels, so it can also use yearly periods.
export type WatchlistTotalProps = WatchlistReportProps & { timeStep: WatchlistChartProps['timeStep'] | 'years' };

export type WatchlistBarChartPeriod = {
  total: number;
  category: string;
  custom: string;
  startOn: string;
};

type SeparatedWatchlistRecordTransactions = SeparatedWatchlistTransactions<TransactionRecord, TransactionItem>;

export type WatchlistDisplayGroup = SeparatedWatchlistRecordTransactions & {
  name: string;
  amount: number;
  percentage: number;
  txns: TransactionRecord[];
  txnItems: TransactionItem[];
};

export type WatchlistDisplayPeriod = SeparatedWatchlistRecordTransactions & {
  timeStamp: string;
  total: number;
  txns: TransactionRecord[];
  txnItems: TransactionItem[];
  miniCardDataArray: WatchlistDisplayGroup[];
};

export type WatchlistDisplayData = {
  total: number;
  allTxns: ImmutableList<TransactionRecord>;
  allTxnItems: ImmutableList<TransactionItem>;
  allTxnsWithoutUpcoming: TransactionRecord[];
  allTxnsUpcoming: TransactionRecord[];
  allTxnsItemsWithoutUpcoming: TransactionItem[];
  allTxnsItemsUpcoming: TransactionItem[];
  chartData: { periods: WatchlistDisplayPeriod[] };
  trueChartData: Array<
    Omit<WatchlistDisplayPeriod, 'txns' | 'txnItems'> & {
      txns: undefined;
      txnItems: undefined;
      category: string;
      custom: string;
    }
  >;
  allGroups: WatchlistDisplayGroup[];
};

/**
 * Share of each slice in the watchlist breakdown (mini cards, allGroups).
 * Always use gross (sum of |group totals|) so mixed income + expense does not divide by a
 * small net (which produced 800%+ slices) and net-zero months still sum to 100%.
 */
function watchlistGroupSharePercent(
  groupTotal: number,
  netTotal: number,
  groupsRecord: Record<string, ReportGroupTotal> | null | undefined,
): number {
  const grossTotal = Object.values(groupsRecord || {}).reduce((sum, g) => sum + Math.abs(g?.total ?? 0), 0);
  if (grossTotal > 0) {
    return Number(((Math.abs(groupTotal) / grossTotal) * 100).toFixed(2));
  }
  if (Math.abs(netTotal) > 0) {
    return Number(Math.abs((groupTotal / netTotal) * 100).toFixed(2));
  }
  return 0;
}

export const getSpendingWatchListStore = (state: State): SpendingWatchlistStore =>
  state[SPENDING_WATCH_LIST_REDUCER_KEY];

function getSwlDateRange(watchlist: SpendingWatchlist): Interval {
  const startDate = DateTime.fromISO(watchlist.startDate ?? '');
  if (watchlist.period === 'FIXED' && startDate.isValid) {
    // Always use the configured start/end so the chart x-axis spans the full
    // watchlist period. Active watchlists show empty bars for future months;
    // reminders beyond endDate are excluded by the interval filter.
    const endDate = DateTime.fromISO(watchlist.endDate ?? '');
    // With no end date the range runs through today (or its start day, if later), as
    // computeWatchlistFixedMetrics counts it, instead of failing every report on the watchlist.
    return Interval.fromDateTimes(
      startDate,
      endDate.isValid ? endDate : DateTime.max(startDate, DateTime.local().startOf('day')),
    );
  }
  const monthEnd = DateTime.local().endOf('month');
  return constructPredefinedInterval({ value: 'RECENT_12_MONTHS' }, monthEnd);
}

// Payees are excluded from filterObjectFn because getOverTimeData only filters on splits,
// and splits don't carry payee data — so a payee filter would always return 0 matches.
function buildWatchlistFilter(
  filterObjectFn: ReturnType<typeof transactionListUtils.createFilterFromFilterObject>,
  reportSpecificFilter: ReportSpecificFilter,
  filterType: string | undefined,
): ReportSpecificFilter {
  if (!filterObjectFn) return reportSpecificFilter;
  return (txn, categoriesById): boolean => {
    if (!reportSpecificFilter(txn, categoriesById)) return false;
    if (filterType === 'payees') return true;
    // The helper preserves its input container; this invocation always returns an array.
    const filtered = filterObjectFn([txn]);
    return Array.isArray(filtered) && filtered.length > 0;
  };
}

// Compute FIXED-period metrics as a pure function so callers can memoize it
// without depending on the selector cache (which has size 1 and misses on every store update).
// Takes an ISO date string rather than a DateTime to keep luxon out of the card layer.
export function computeWatchlistFixedMetrics(
  watchlist: WatchlistMetricsInput | null | undefined,
  todayISO: string,
): WatchlistFixedMetrics | null {
  if (!watchlist || watchlist.period !== 'FIXED') return null;
  const startDate = DateTime.fromISO(watchlist.startDate ?? '');
  const endDate = DateTime.fromISO(watchlist.endDate ?? '');
  const today = DateTime.fromISO(todayISO);
  const hasEndDate = endDate.isValid;
  // A watchlist that ends today is still active; only count it as ended after today.
  const isEnded = hasEndDate && today.startOf('day') > endDate.startOf('day');

  // Inclusive of today: an active watchlist on its end date still has 1 day left, which matches
  // isEnded treating the end date as active. Ended watchlists show 0; undated ones have no
  // countdown to display.
  const daysLeft = !hasEndDate
    ? null
    : isEnded
      ? 0
      : Math.max(0, Math.round(endDate.startOf('day').diff(today.startOf('day'), 'days').days) + 1);
  // Without an endDate, endDate is Invalid, so the whole-period total must fall back to today
  // or every total/average derived from it goes NaN.
  const totalDaysEndDate = hasEndDate ? endDate : today;
  const totalDays = Math.round(totalDaysEndDate.diff(startDate, 'days').days) + 1;
  const effectiveEndDate = isEnded ? endDate : today;
  // Elapsed-so-far drives projection pace; displayed averages use the full rendered range below.
  const daysElapsed = Math.max(1, Math.round(effectiveEndDate.diff(startDate, 'days').days));
  // 0 is a valid spent amount (no transactions yet); a falsy check would suppress the projected total.
  const spentAbs = watchlist.spentSoFar != null ? Math.abs(watchlist.spentSoFar) : null;
  const pace = spentAbs != null ? spentAbs / daysElapsed : null;
  const projected = pace != null && totalDays > 0 ? pace * totalDays : null;

  // The displayed average follows the chart's granularity and includes every rendered period,
  // including future zero-value bars in an active range.
  const timeStep = getFixedWatchlistTimeStep(watchlist.startDate, watchlist.endDate);
  const totalMonths = computeMonthsInRange(watchlist.startDate, watchlist.endDate);
  const dailyAverage = spentAbs != null && timeStep === 'days' && totalDays > 0 ? spentAbs / totalDays : null;
  const monthlyAverage = spentAbs != null && timeStep === 'months' && totalMonths > 0 ? spentAbs / totalMonths : null;

  // targetAmount is stored in ledger sign: compare magnitudes, or a negative target reads as a
  // large overspend.
  const remaining = watchlist.targetAmount != null ? Math.abs(watchlist.targetAmount) - (spentAbs || 0) : null;

  return {
    isEnded,
    isActive: !isEnded,
    daysLeft,
    totalDays,
    totalMonths,
    daysElapsed,
    spentAbs,
    timeStep,
    dailyAverage,
    monthlyAverage,
    projected,
    remaining,
  };
}

// Centralizes all FIXED-period derived math so the detail view and
// card don't each reimplement daysLeft / projected with subtle differences.
// A weakmap selector so a multi-watchlist view (e.g. a list of cards) gets one cache entry
// per watchlist instead of a single entry that misses on every other watchlist's render.
export const getWatchlistFixedMetrics = createWeakMapSelector(
  (state: FixedMetricsState, props: { id: string }): SpendingWatchlist | undefined =>
    getSpendingWatchListById(state, props.id),
  (state: FixedMetricsState): string => dateSelectors.getCurrentDateString(state),
  (watchlist, todayISO): WatchlistFixedMetrics | null => computeWatchlistFixedMetrics(watchlist, todayISO),
);

export const getIsLoading = (state: State): boolean => getSpendingWatchListStore(state).isLoading;
export const getLoadPending = (state: State): boolean => getSpendingWatchListStore(state).loadPending;
export const getError = (state: State): unknown => getSpendingWatchListStore(state).error;
export const getLastSyncDate = (state: State): SpendingWatchlistStore['lastSyncDate'] =>
  getSpendingWatchListStore(state).lastSyncDate;

// A create is optimistic: the record lands in the store keyed by its clientId with `id: null` and
// stays that way until the server accepts it. Everything downstream addresses a watchlist by its
// server id, so an id-less record is unroutable — it cannot be opened, edited or deleted, and a
// create that never succeeds leaves one behind for the whole session. Filter it out here, at the
// one selector every consumer goes through, so the list, the counts, the zero states and the name
// uniqueness rule all answer "do I have any watchlists?" the same way. The cost is that a create
// is not visible until its POST returns.
export const getSpendingWatchLists = createSelector(
  (state: State): SpendingWatchlistStore['resourcesById'] => getSpendingWatchListStore(state).resourcesById,
  (resourcesById): ImmutableMap<string, SavedSpendingWatchlist> => resourcesById.filter(isSavedSpendingWatchlist),
);

// Deliberately reads the raw store: callers look up by server id, and the pending records this
// skips are keyed by clientId anyway.
export const getSpendingWatchListById = (state: State, id: string): SpendingWatchlist | undefined =>
  getSpendingWatchListStore(state).resourcesById.get(id);

export const getSpendingWatchListData = createSelector(
  (state: State) => getSpendingWatchLists(state),
  (spendingWatchLists): ImmutableList<Watchlist> => {
    let data = ImmutableList<Watchlist>();
    if (!spendingWatchLists) {
      return data;
    }
    let index = 0;
    spendingWatchLists.forEach((swl) => {
      data = data.push({
        id: swl.id,
        filterId: swl.filterId,
        name: swl.name,
        thisMonth: swl.thisMonth,
        yearToDate: swl.yearToDate,
        monthlyTrend: swl.monthlyTrend,
        numMonthlyTrendMonths: swl.numMonthlyTrendMonths,
        monthSpendingProjection: swl.monthSpendingProjection,
        targetAmount: swl.targetAmount,
        emoji: swl.emoji,
        createdAt: swl.createdAt,
        period: swl.period,
        startDate: swl.startDate,
        endDate: swl.endDate,
        spentSoFar: swl.spentSoFar,
        order: index,
      });
      index += 1;
    });
    return data;
  },
);

export const getSortedWatchlists = createSelector(
  (state: State): ImmutableList<Watchlist> => getSpendingWatchListData(state),
  (_state: State, sortOrder?: WatchlistSortOrder): WatchlistSortOrder | undefined => sortOrder,
  (spendingWatchLists, sortOrder): ImmutableList<Watchlist> => sortWatchList(spendingWatchLists, sortOrder),
);

const getWatchlistReportLoadPending = (state: ChartState): boolean =>
  accountsSelectors.getLoadPending(state) ||
  categoriesSelectors.getLoadPending(state) ||
  transactionsSelectors.getLoadPending(state) ||
  tagsSelectors.getLoadPending(state);

// Inputs shared by the chart, detail and total selectors.
const selectWatchlist = (state: ChartState, props: WatchlistReportProps) => getSpendingWatchListById(state, props.id);
const selectFilterObjectFn = (_state: ChartState, props: WatchlistReportProps) =>
  transactionListUtils.createFilterFromFilterObject(props.filterObject);
const selectFilterType = (_state: ChartState, props: WatchlistReportProps) => props.filterType;
const selectCategoriesById = (state: ChartState) => categoriesSelectors.getCategoriesById(state);

// Inputs shared by the chart and detail selectors.
const selectChartTxns = (state: ChartState, props: WatchlistChartProps) =>
  getReportFilteredTxns(state, {
    ...props,
    upcomingTxnsIncluded: true,
    reportSpecificFilter: getReportSpecificFilterFromReportName(props.reportName),
  });
const selectTags = (state: ChartState) => tagsSelectors.getTags(state);
const selectReportName = (_state: ChartState, props: WatchlistChartProps) => props.reportName;
const selectChartTimeStep = (_state: ChartState, props: WatchlistChartProps) => props.timeStep;
const selectChartType = (_state: ChartState, props: WatchlistChartProps) => props.chartType;
const selectReportSpecificFilter = (_state: ChartState, props: WatchlistChartProps) =>
  getReportSpecificFilterFromReportName(props.reportName);

export const getWatchlistDataForBarChart = createSelector(
  getWatchlistReportLoadPending,
  selectChartTxns,
  selectCategoriesById,
  selectTags,
  selectReportName,
  selectChartTimeStep,
  selectChartType,
  selectFilterType,
  selectReportSpecificFilter,
  selectFilterObjectFn,
  selectWatchlist,
  (
    loadPending,
    txnsList,
    categoriesById,
    tagsById,
    reportName,
    timeStep,
    chartType,
    filterType,
    reportSpecificFilter,
    filterObjectFn,
    watchlist,
  ): WatchlistBarChartPeriod[] | null => {
    if (loadPending || !txnsList || !watchlist) {
      return null;
    }
    const cacheKey = selectorsHelpers.getOrCreateStringFromObject(
      'watchlist',
      {
        txnsList,
        reportName,
        timeStep,
        chartType,
        reportSpecificFilter,
        categoriesById,
        tagsById,
        filterObjectFn,
        filterType,
        watchlist: JSON.stringify(watchlist),
      },
      20,
      selectorsHelpers.memoizeWithKeyRetrieveCache,
    );
    return selectorsHelpers.memoizeWithKey(
      'watchlist-graph-data',
      (): WatchlistBarChartPeriod[] | null => {
        const swlDateRange = getSwlDateRange(watchlist);
        const filterIncludingAppliedFilters = buildWatchlistFilter(filterObjectFn, reportSpecificFilter, filterType);
        const report = getOverTimeData(
          txnsList,
          swlDateRange,
          timeStep,
          null,
          categoriesById,
          tagsById,
          filterIncludingAppliedFilters,
        );

        const isDailyDisplay = timeStep === 'days';
        const periods =
          report
            ?.get('periods')
            ?.map((period) => ({
              total: -1 * period.total,
              category: DateTime.fromISO(period.timeStamp).toFormat(isDailyDisplay ? 'M/d' : 'LLL'),
              custom: DateTime.fromISO(period.timeStamp).toFormat(isDailyDisplay ? 'M/d/yyyy' : 'LLLL yyyy'),
              // Raw ISO timestamp so buildDateAxisLabels can compute smart x-axis labels;
              // `category` is already formatted (e.g. "Jan") and loses the date information.
              startOn: period.timeStamp,
            }))
            .toArray() || null;

        return periods;
      },
      cacheKey,
      20,
    );
  },
);

// A card needs only the amount; invalid contexts must not enter the report transaction graph.
const getWatchlistForTotal = createWeakMapSelector(
  selectWatchlist,
  getWatchlistReportLoadPending,
  (watchlist, loadPending): SpendingWatchlist | undefined => (loadPending ? undefined : watchlist),
);

const watchlistReportFilter = getReportSpecificFilterFromReportName('watchlist');
const whenWatchlistReady =
  <Result>(select: (state: WatchlistTotalState, props: WatchlistTotalProps) => Result) =>
  (state: WatchlistTotalState, props: WatchlistTotalProps): Result | null =>
    getWatchlistForTotal(state, props) ? select(state, props) : null;

// Bound before use: inline generic calls in an input list widen the selector's arguments to any.
const getReadyTotalTxns = whenWatchlistReady((state, props) =>
  getReportFilteredTxns(state, {
    filterObject: props.filterObject,
    upcomingTxnsIncluded: true,
    reportSpecificFilter: watchlistReportFilter,
  }),
);
const getReadyTotalCategories = whenWatchlistReady(selectCategoriesById);
const getReadyTotalFilter = whenWatchlistReady(selectFilterObjectFn);

export const getWatchlistTotal = createWeakMapSelector(
  getWatchlistForTotal,
  getReadyTotalTxns,
  getReadyTotalCategories,
  getReadyTotalFilter,
  (_state: WatchlistTotalState, props: WatchlistTotalProps) => props.timeStep,
  selectFilterType,
  (watchlist, transactions, categories, filterObjectFn, timeStep, filterType): number | null => {
    // categories shares the watchlist's readiness gate; checking it only narrows the type.
    if (!watchlist || !transactions || !categories) return null;
    return getOverTimeTotal(
      transactions,
      getSwlDateRange(watchlist),
      timeStep,
      categories,
      buildWatchlistFilter(filterObjectFn, watchlistReportFilter, filterType),
    );
  },
);

export const getWatchlistDataForDisplay = createSelector(
  getWatchlistReportLoadPending,
  selectChartTxns,
  selectCategoriesById,
  selectTags,
  selectReportName,
  selectChartTimeStep,
  selectChartType,
  (_state: ChartState, props: WatchlistDisplayProps) => props.groupBy,
  selectFilterType,
  selectReportSpecificFilter,
  selectFilterObjectFn,
  selectWatchlist,
  (
    loadPending,
    txnsList,
    categoriesById,
    tagsById,
    reportName,
    timeStep,
    chartType,
    groupBy,
    filterType,
    reportSpecificFilter,
    filterObjectFn,
    watchlist,
  ): WatchlistDisplayData | null => {
    if (loadPending || !txnsList || !watchlist) {
      return null;
    }

    const cacheKey = selectorsHelpers.getOrCreateStringFromObject(
      'watchlist',
      {
        txnsList,
        reportName,
        timeStep,
        chartType,
        groupBy,
        reportSpecificFilter,
        categoriesById,
        tagsById,
        filterObjectFn,
        filterType,
        watchlist: JSON.stringify(watchlist),
      },
      6,
      selectorsHelpers.memoizeWithKeyRetrieveCache,
    );
    return selectorsHelpers.memoizeWithKey(
      'watchlist-graph-data',
      (): WatchlistDisplayData => {
        const swlDateRange = getSwlDateRange(watchlist);
        const filterIncludingAppliedFilters = buildWatchlistFilter(filterObjectFn, reportSpecificFilter, filterType);
        const report = getOverTimeData(
          txnsList,
          swlDateRange,
          timeStep,
          groupBy ?? null,
          categoriesById,
          tagsById,
          filterIncludingAppliedFilters,
        );
        const allTxns = report.get('allTxns');
        const allTxnItems = report.get('allTxnItems');
        const { txnsWithoutUpcoming, txnsUpcoming } = separateFutureAndPastTxns(allTxns.toArray());
        const { txnsItemsWithoutUpcoming, txnsItemsUpcoming } = separateFutureAndPastTxns(allTxnItems.toArray());

        const periods = report
          .get('periods')
          .map((period): WatchlistDisplayPeriod => {
            const miniCardDataArray: WatchlistDisplayGroup[] = [];
            if (period.groups) {
              Object.entries(period.groups).forEach(([groupKey, groupData]) => {
                miniCardDataArray.push({
                  name: groupKey,
                  amount: groupData.total,
                  percentage: watchlistGroupSharePercent(groupData.total, period.total, period.groups),
                  txns: groupData.txns,
                  txnItems: groupData.txnItems,
                  ...separateFutureAndPastTxns(groupData.txnItems),
                });
              });
            }
            return {
              timeStamp: period.timeStamp,
              total: -1 * period.total,
              txns: period.txns,
              txnItems: period.txnItems,
              ...separateFutureAndPastTxns(period.txnItems),
              miniCardDataArray: miniCardDataArray.sort((a, b) => b.percentage - a.percentage),
            };
          })
          .toArray();

        const isDailyDisplay = timeStep === 'days';
        const trueChartData: WatchlistDisplayData['trueChartData'] = periods.map((per) => ({
          ...per,
          txns: undefined, // remove txns for optimization
          txnItems: undefined,
          category: DateTime.fromISO(per.timeStamp).toFormat(isDailyDisplay ? 'M/d' : 'LLL'),
          custom: DateTime.fromISO(per.timeStamp).toFormat(isDailyDisplay ? 'M/d/yyyy' : 'LLLL yyyy'),
        }));

        const allGroupsForReport = report.get('allGroups') || {};
        const allGroups = Object.entries(allGroupsForReport).map(([groupKey, amount]): WatchlistDisplayGroup => {
          const { total } = amount;
          const txns = periods.reduce<TransactionRecord[]>((accum, period) => {
            const groupTxnsFromPeriod = period.miniCardDataArray.find((item) => item.name === groupKey)?.txns;
            return groupTxnsFromPeriod ? [...accum, ...groupTxnsFromPeriod] : accum;
          }, []);
          const txnItems = periods.reduce<TransactionItem[]>((accum, period) => {
            const groupTxnsFromPeriod = period.miniCardDataArray.find((item) => item.name === groupKey)?.txnItems;
            return groupTxnsFromPeriod ? [...accum, ...groupTxnsFromPeriod] : accum;
          }, []);

          return {
            name: groupKey,
            amount: total,
            percentage: watchlistGroupSharePercent(total, report.get('total'), allGroupsForReport),
            txns,
            txnItems,
            ...separateFutureAndPastTxns(txnItems),
          };
        });

        return {
          total: report.get('total'),
          allTxns,
          allTxnItems,
          allTxnsWithoutUpcoming: txnsWithoutUpcoming,
          allTxnsUpcoming: txnsUpcoming,
          allTxnsItemsWithoutUpcoming: txnsItemsWithoutUpcoming,
          allTxnsItemsUpcoming: txnsItemsUpcoming,
          chartData: { periods },
          trueChartData,
          allGroups,
        };
      },
      cacheKey,
      6,
    );
  },
);

export const getShowSetup = createSelector(
  preferencesSelectors.getWatchlistSetupCompleted,
  authSelectors.getCurrentDataset,
  getSortedWatchlists,
  (state: SetupState) => featureFlagsSelectors.getFeatureFlag(state, 'watchlistSetup'),
  (watchlistSetupCompleted, currentDataset, spendingWatchListArray, watchlistSetup): boolean => {
    if (!currentDataset) return false;
    const swlSetup = watchlistSetup
      ? DateTime.fromISO(currentDataset.createdAt ?? '').toMillis() > Number(watchlistSetup) * 1000
      : false;
    return swlSetup && spendingWatchListArray.size === 0 && !watchlistSetupCompleted;
  },
);
